import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CalendarMonth as CalendarIcon,
  Refresh as RefreshIcon,
  BeachAccess as BeachAccessIcon,
  MedicalServices as MedicalIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  People as PeopleIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts'
import { usePagination, useLoading, useDialog, useTableFilters } from '../../hooks'
import { StatusChip } from '../../components/common/Chips'
import { formatDate } from '../../utils/formatters'
import { leaveApi } from '../../api/leaveApi'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'

const LeaveManagement = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state) => state.auth)
  const { loading, withLoading } = useLoading()
  const pagination = usePagination()
  const dialog = useDialog()
  const { filters, updateFilter } = useTableFilters({
    status: 'all',
    type: 'all',
    year: new Date().getFullYear().toString(),
  })


  const [leaveConfigMissing, setLeaveConfigMissing] = useState(false)

  const [leaveData, setLeaveData] = useState({
    leaveBalance: {
      casual: { total: 0, taken: 0, remaining: 0 },
      sick: { total: 0, taken: 0, remaining: 0 },
      earned: { total: 0, taken: 0, remaining: 0 },
      maternity: { total: 0, taken: 0, remaining: 0 },
      paternity: { total: 0, taken: 0, remaining: 0 },
      unpaid: { total: 0, taken: 0, remaining: 0 },
      compensatory: { total: 0, taken: 0, remaining: 0 },
    },
    leaveStats: {
      totalApplied: 0,
      approved: 0,
      pending: 0,
      rejected: 0,
      cancellationRequests: 0,
    },
    leaveHistory: [],
    upcomingHolidays: [],
    leaveStatistics: {
      casual: 0,
      sick: 0,
      paid: 0,
      maternity: 0,
      paternity: 0,
      unpaid: 0
    }
  })

  const [detailedLeaveData, setDetailedLeaveData] = useState(null)

  useEffect(() => {
    if (user?.id) {
      fetchLeaveData()
    }
  }, [user, filters.year])

  const fetchLeaveData = async () => {
    if (!user?.id) return

    withLoading(async () => {
      try {
        const response = await leaveApi.getLeaveHistory(user.id)
        const historyData = response.data.leave_history || []

        // Initialize stats
        let totalApplied = 0
        let approved = 0
        let pending = 0
        let rejected = 0
        let cancelled = 0

        // Helper to parse DD-MM-YYYY or standard dates
        const parseDate = (dateStr) => {
          if (!dateStr) return null

          // Check if it matches DD-MM-YYYY format
          if (typeof dateStr === 'string' && /^\d{2}-\d{2}-\d{4}/.test(dateStr)) {
            const [day, month, year] = dateStr.split('-').map(Number)
            return new Date(year, month - 1, day)
          }

          const date = new Date(dateStr)
          return isNaN(date.getTime()) ? null : date
        }


        const mappedHistory = historyData.map(leave => {

          const parseDateForCalc = (dateStr) => {
            if (!dateStr) return null
            if (typeof dateStr === 'string' && /^\d{2}-\d{2}-\d{4}/.test(dateStr)) {
              const [day, month, year] = dateStr.split('-').map(Number)
              return new Date(year, month - 1, day)
            }
            const date = new Date(dateStr)
            return isNaN(date.getTime()) ? null : date
          }

          const startDate = parseDateForCalc(leave.start_date)
          const endDate = parseDateForCalc(leave.end_date)
          const appliedDate = parseDateForCalc(leave.applied_on) || parseDateForCalc(leave.created_at)

          // Calculate days
          let days = 0
          if (startDate && endDate) {
            const diffTime = Math.abs(endDate - startDate)
            days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
          } else if (leave.duration === 'full day') {
            days = 1
          } else if (leave.duration === 'half day') {
            days = 0.5
          }

          return {
            id: leave.id,
            leaveId: leave.leave_code,
            type: leave.leave_type,
            from: formatDate(leave.start_date),
            to: formatDate(leave.end_date),
            days: days,
            appliedOn: formatDate(appliedDate),
            appliedDate: appliedDate,
            status: leave.status.charAt(0).toUpperCase() + leave.status.slice(1),
            reason: leave.reason,
            approvedBy: leave.approved_by || '-',
            approvedOn: formatDate(leave.approved_at),
            remarks: leave.remarks || '-',
            createdAt: leave.created_at
          }
        }).sort((a, b) => {
          // Sort by applied date (newest first)
          const dateA = a.appliedDate || new Date(0)
          const dateB = b.appliedDate || new Date(0)
          return dateB - dateA
        })


        try {
          const statsResponse = await leaveApi.getLeaveStats(user.id)
          const statsData = statsResponse.data

          totalApplied = statsData.total_leaves_applied || totalApplied
          approved = statsData.approved || approved
          pending = statsData.pending || pending
          rejected = statsData.rejected || rejected
          cancelled = statsData.cancelled || cancelled

        } catch (error) {
          console.error('Failed to fetch leave stats:', error)
        }


        try {
          const statisticsResponse = await leaveApi.getLeaveStatistics(user.id)
          const statisticsData = statisticsResponse.data.leave_statistics || {}

          setLeaveData(prev => ({
            ...prev,
            leaveStatistics: {
              casual: statisticsData.casual || 0,
              sick: statisticsData.sick || 0,
              paid: statisticsData.paid || 0,
              maternity: statisticsData.maternity || 0,
              paternity: statisticsData.paternity || 0,
              compensatory: statisticsData.compensatory || 0,
              unpaid: statisticsData.unpaid || 0,
            }
          }))

        } catch (error) {
          console.error('Failed to fetch leave statistics:', error)
        }

        // Fetch leave balance
        try {
          const balanceResponse = await leaveApi.getLeaveBalance(user.id)
          const balanceData = balanceResponse.data

          // Map API balance data to state structure
          // API returns: { sick_leaves: { total, used, available }, ... }
          // State expects: { sick: { total, taken, remaining }, ... }
          const leaveBalance = {
            casual: {
              total: balanceData.casual_leaves?.total || 0,
              taken: balanceData.casual_leaves?.used || 0,
              remaining: balanceData.casual_leaves?.available || 0
            },
            sick: {
              total: balanceData.sick_leaves?.total || 0,
              taken: balanceData.sick_leaves?.used || 0,
              remaining: balanceData.sick_leaves?.available || 0
            },
            paid: {
              total: balanceData.paid_leaves?.total || 0,
              taken: balanceData.paid_leaves?.used || 0,
              remaining: balanceData.paid_leaves?.available || 0
            },
            maternity: {
              total: balanceData.maternity_leaves?.total || 0,
              taken: balanceData.maternity_leaves?.used || 0,
              remaining: balanceData.maternity_leaves?.available || 0
            },
            paternity: {
              total: balanceData.paternity_leaves?.total || 0,
              taken: balanceData.paternity_leaves?.used || 0,
              remaining: balanceData.paternity_leaves?.available || 0
            },
            unpaid: {
              total: balanceData.unpaid_leaves?.total || 0,
              taken: balanceData.unpaid_leaves?.used || 0,
              remaining: balanceData.unpaid_leaves?.available || 0
            },
            compensatory: {
              total: balanceData.compensatory_leaves?.total || 0,
              taken: balanceData.compensatory_leaves?.used || 0,
              remaining: balanceData.compensatory_leaves?.available || 0
            },
          }

          setLeaveData(prev => ({
            ...prev,
            leaveBalance,
            leaveStats: {
              totalApplied,
              approved,
              pending,
              rejected,
              cancellationRequests: cancelled,
            },
            leaveHistory: mappedHistory,
            upcomingHolidays: [], // This would come from a separate API
          }))

        } catch (error) {
          console.error('Failed to fetch leave balance:', error)
          // Check if leave configuration is missing for the org
          const detail = error?.response?.data?.detail || ''
          if (error?.response?.status === 404 && typeof detail === 'string' && detail.toLowerCase().includes('leave balance configuration not found')) {
            setLeaveConfigMissing(true)
          }
          // Fallback to calculating from history if balance API fails
          // Calculate leave balance by type (from history data)
          const leaveBalance = {
            casual: { total: 0, taken: 0, remaining: 0 },
            sick: { total: 0, taken: 0, remaining: 0 },
            paid: { total: 0, taken: 0, remaining: 0 },
            maternity: { total: 0, taken: 0, remaining: 0 },
            paternity: { total: 0, taken: 0, remaining: 0 },
            unpaid: { total: 0, taken: 0, remaining: 0 },
            compensatory: { total: 0, taken: 0, remaining: 0 },
          }

          historyData.forEach(leave => {
            const type = leave.leave_type.toLowerCase()
            let days = 0
            if (leave.start_date && leave.end_date) {
              const start = new Date(leave.start_date)
              const end = new Date(leave.end_date)
              const diffTime = Math.abs(end - start)
              days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
            } else if (leave.duration === 'full day') {
              days = 1
            } else if (leave.duration === 'half day') {
              days = 0.5
            }

            if (leaveBalance[type]) {
              leaveBalance[type].taken += days
            }
          })

          setLeaveData(prev => ({
            ...prev,
            leaveBalance,
            leaveStats: {
              totalApplied,
              approved,
              pending,
              rejected,
              cancellationRequests: cancelled,
            },
            leaveHistory: mappedHistory,
            upcomingHolidays: [],
          }))
        }
      } catch (error) {
        console.error('Failed to fetch leave data:', error)
        toast.error('Failed to fetch leave data')
      }
    })
  }

  const handleCancelLeave = async (leaveId) => {
    if (window.confirm('Are you sure you want to cancel this leave request?')) {
      withLoading(async () => {
        try {
          await leaveApi.cancelLeave(leaveId, { skipSuccessToast: true })
          toast.success('Leave request cancelled successfully')
          await fetchLeaveData() // Refresh data
        } catch (error) {
          console.error('Failed to cancel leave:', error)
        }
      })
    }
  }

  const fetchLeaveDetails = async (leaveId) => {
    try {
      const response = await leaveApi.getLeaveDetails(leaveId)
      setDetailedLeaveData(response.data)
      dialog.openDialog({ id: leaveId }) // Open dialog with leave ID
    } catch (error) {
      console.error('Failed to fetch leave details:', error)
      toast.error('Failed to load leave details')
    }
  }

  const handleCloseDialog = () => {
    dialog.closeDialog()
    setDetailedLeaveData(null) // Clear data when closing
  }

  const getLeaveTypeIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'casual leave': return <BeachAccessIcon />
      case 'sick leave': return <MedicalIcon />
      case 'earned leave': return <EventAvailableIcon />
      case 'maternity leave': return <PeopleIcon />
      case 'paternity leave': return <PeopleIcon />
      case 'compensatory leave': return <EventAvailableIcon />
      case 'unpaid leave': return <EventBusyIcon />
      case 'work from home': return <WorkIcon />
      case 'study leave': return <SchoolIcon />
      default: return <BeachAccessIcon />
    }
  }

  const leaveTypeColors = {
    'Casual Leave': '#4caf50',
    'Sick Leave': '#2196f3',
    'Paid Leave': '#ff9800',
    'Maternity Leave': '#e91e63',
    'Paternity Leave': '#9c27b0',
    'Compensatory Leave': '#00bcd4',
    'Unpaid Leave': '#607d8b',
  }

  // Always show all 6 leave types in the chart
  const leaveChartData = [
    { name: 'Casual Leave', value: leaveData.leaveStatistics?.casual || 0, color: leaveTypeColors['Casual Leave'] },
    { name: 'Sick Leave', value: leaveData.leaveStatistics?.sick || 0, color: leaveTypeColors['Sick Leave'] },
    { name: 'Paid Leave', value: leaveData.leaveStatistics?.paid || 0, color: leaveTypeColors['Paid Leave'] },
    { name: 'Maternity Leave', value: leaveData.leaveStatistics?.maternity || 0, color: leaveTypeColors['Maternity Leave'] },
    { name: 'Paternity Leave', value: leaveData.leaveStatistics?.paternity || 0, color: leaveTypeColors['Paternity Leave'] },
    { name: 'Compensatory Leave', value: leaveData.leaveStatistics?.compensatory || 0, color: leaveTypeColors['Compensatory Leave'] },
    { name: 'Unpaid Leave', value: leaveData.leaveStatistics?.unpaid || 0, color: leaveTypeColors['Unpaid Leave'] },
  ]

  const filteredLeaves = leaveData.leaveHistory.filter(leave => {
    if (filters.status !== 'all' && leave.status.toLowerCase() !== filters.status.toLowerCase()) {
      return false
    }
    if (filters.type !== 'all' && leave.type.toLowerCase() !== filters.type) {
      return false
    }
    return true
  })

  // Use pagination on filtered data
  const paginatedLeaves = filteredLeaves.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  )

  // Define all available leave types 
  const leaveTypes = [
    { value: 'casual', label: 'Casual Leave' },
    { value: 'sick', label: 'Sick Leave' },
    { value: 'paid', label: 'Paid Leave' },
    { value: 'maternity', label: 'Maternity Leave' },
    { value: 'paternity', label: 'Paternity Leave' },
    { value: 'compensatory', label: 'Compensatory Leave' },
    { value: 'unpaid', label: 'Unpaid Leave' }
  ]

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Leave Management
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Manage your leave applications, view balance, and track approvals
              </Typography>
            </Box>
            {!leaveConfigMissing && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/employee/leave/apply')}
                size="large"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Apply for Leave
              </Button>
            )}
          </Box>

          {leaveConfigMissing && (
            <Alert
              severity="warning"
              sx={{ mb: 3, borderRadius: 2 }}
            >
              <strong>Leave policy not configured.</strong> Your organization has not set up leave configuration yet. Please contact your administrator to configure leave settings before you can view balances or apply for leave.
            </Alert>
          )}

          {/* Quick Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                        {leaveData.leaveStats.totalApplied}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Applied
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                        {leaveData.leaveStats.pending}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Pending
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                        {leaveData.leaveStats.rejected}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Rejected
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                        {leaveData.leaveStats.cancellationRequests}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Cancellation
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                        {leaveData.leaveStats.approved}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Approved
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

        </Box>

        {/* Leave Balance and Chart */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Leave Balance Section */}
          <Grid size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Leave Balance
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(leaveData.leaveBalance).map(([type, data], index) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {getLeaveTypeIcon(type.charAt(0).toUpperCase() + type.slice(1) + ' Leave')}
                          <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'medium' }}>
                            {type.charAt(0).toUpperCase() + type.slice(1)} Leave
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(data.taken / data.total) * 100}
                          sx={{ mt: 2, height: 6, borderRadius: 3 }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="textSecondary">Total</Typography>
                            <Typography variant="body2" fontWeight="bold">{data.total}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="textSecondary">Used</Typography>
                            <Typography variant="body2" fontWeight="bold">{data.taken}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="textSecondary">Balance</Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary">{data.remaining}</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Leave Statistics Section */}
          <Grid size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom>
                Leave Statistics
              </Typography>
              {leaveChartData.every(d => d.value === 0) ? (
                <Box sx={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                  <CalendarIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                  <Typography variant="body2" color="textSecondary">
                    No leave data available yet
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Statistics will appear once you have approved leaves
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: '100%', height: 320, mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={leaveChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={({ name, percent }) => percent > 0 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {leaveChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Filters and Leave History */}
        <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => updateFilter('status', e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                label="Type"
                onChange={(e) => updateFilter('type', e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                {leaveTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              onClick={fetchLeaveData}
              disabled={loading}
              variant="outlined"
              sx={{ flex: 1, minWidth: 120 }}
            >
              <RefreshIcon sx={{ mr: 1 }} aria-hidden="false" />
              Refresh
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Leave ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell>Applied On</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton columns={8} rows={pagination.rowsPerPage} />
                ) : paginatedLeaves.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CalendarIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body1" color="textSecondary">
                        No leave history found
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Your leave applications will appear here
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedLeaves.map((leave) => (
                    <TableRow key={leave.id} hover>
                      <TableCell>{leave.leaveId}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getLeaveTypeIcon(leave.type)}
                          <Typography variant="body2">{leave.type}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{leave.from}</TableCell>
                      <TableCell>{leave.to}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${leave.days} day${leave.days > 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{leave.appliedOn}</TableCell>
                      <TableCell>
                        <StatusChip status={leave.status} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => fetchLeaveDetails(leave.id)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {leave.status === 'Pending' && (
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                onClick={() => handleCancelLeave(leave.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredLeaves.length}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        </Paper >

        {/* Leave Details Dialog */}
        <Dialog open={dialog.isOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          {detailedLeaveData && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Leave Details - {detailedLeaveData.leave_code}</Typography>
                  <StatusChip status={detailedLeaveData.other_info.status} />
                </Box>
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2}>
                  {/* Leave Information */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">Leave Type</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ textTransform: 'capitalize', mb: 2 }}>
                      {detailedLeaveData.leave_info.leave_type}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">Duration</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {detailedLeaveData.leave_info.days_requested} days ({detailedLeaveData.leave_info.duration})
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">From Date</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {formatDate(detailedLeaveData.leave_info.from_date)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">To Date</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {formatDate(detailedLeaveData.leave_info.to_date)}
                    </Typography>
                  </Grid>

                  {/* Leave Balance */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>Leave Balance</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip label={`Applied: ${detailedLeaveData.leave_info.leave_balance.applied_for}`} size="small" />
                      <Chip label={`Total: ${detailedLeaveData.leave_info.leave_balance.total}`} size="small" variant="outlined" />
                      <Chip label={`Remaining: ${detailedLeaveData.leave_info.leave_balance.remaining}`} size="small" color="success" />
                    </Box>
                  </Grid>

                  {/* Reason */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="textSecondary">Reason</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {detailedLeaveData.additional_info.reason}
                    </Typography>
                  </Grid>

                  {/* Work Handover */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">Work Handover To</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {detailedLeaveData.additional_info.work_handover_to_name}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" color="textSecondary">Emergency Contact</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                      {detailedLeaveData.additional_info.emergency_contact_number}
                    </Typography>
                  </Grid>

                  {/* Work Handover Notes */}
                  {detailedLeaveData.additional_info.work_handover_notes && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="textSecondary">Work Handover Notes</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {detailedLeaveData.additional_info.work_handover_notes}
                      </Typography>
                    </Grid>
                  )}

                  {/* Approval Information */}
                  {detailedLeaveData.other_info.status !== 'pending' && (
                    <>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="textSecondary">
                          {detailedLeaveData.other_info.status === 'rejected' ? 'Rejected By' : 'Approved By'}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {detailedLeaveData.other_info.approved_by_name}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="textSecondary">
                          {detailedLeaveData.other_info.status === 'rejected' ? 'Rejected At' : 'Approved At'}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatDate(detailedLeaveData.other_info.approved_at, 'dd/MM/yyyy')}
                        </Typography>
                      </Grid>
                      {detailedLeaveData.other_info.remarks && (
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="body2" color="textSecondary">Remarks</Typography>
                          <Typography variant="body1">{detailedLeaveData.other_info.remarks}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Paper>
    </Box>
  )
}

export default LeaveManagement